import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Grid,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  EditOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
  RestoreOutlined as RestoreIcon
} from '@mui/icons-material';

import InputField from '../../../../components/formFields/InputField';
import { 
  getDinamicTextsStore,
  createDinamicTextStore,
  updateDinamicTextStore,
  deleteDinamicTextStore,
  restoreDinamicTextStore
} from '../../../../store/ratings/thunks';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const ModalDinamicTexts = ({ open, onClose }: Props) => {
  const dispatch = useDispatch();
  const { handleResponse, ResponseComponents } = useResponseHandler();
  
  const { dinamic_texts_list } = useSelector((state: any) => state.ratingsDevices);
  
  const [editMode, setEditMode] = useState(false);
  const [selectedText, setSelectedText] = useState<any>(null);

  useEffect(() => {
    if (open) {
      dispatch(getDinamicTextsStore());
      setEditMode(false);
      setSelectedText(null);
    }
  }, [open, dispatch]);

  const initialValues = {
    name: selectedText ? (selectedText.name || selectedText.label) : ''
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('El nombre es requerido')
  });

  const handleSubmit = async (values: any, { resetForm }: any) => {
    let result;
    if (editMode && selectedText) {
      result = await dispatch(updateDinamicTextStore(selectedText.id || selectedText.value, values));
    } else {
      result = await dispatch(createDinamicTextStore(values));
    }
    
    const isValid = await handleResponse(result, 'Tipo de Encuesta', true);
    if (isValid) {
      resetForm();
      setEditMode(false);
      setSelectedText(null);
    }
  };

  const handleCancelEdit = (resetForm: any) => {
    resetForm();
    setEditMode(false);
    setSelectedText(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Gestionar Tipos de Encuesta</Typography>
          <IconButton onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ resetForm }) => (
              <Form>
                <Grid container spacing={2} sx={{ mt: 0, alignItems: 'center' }}>
                  <Grid size={{ xs: 12 }}>
                    <InputField
                      fullWidth
                      name="name"
                      label="Nombre del tipo"
                      size="small"
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                    {editMode ? (
                      <Button onClick={() => handleCancelEdit(resetForm)} variant="outlined" color="secondary">
                        Cancelar Edición
                      </Button>
                    ) : (
                      <Button onClick={onClose} variant="outlined" color="secondary">
                        Cerrar
                      </Button>
                    )}
                    <Button type="submit" variant="contained">
                      {editMode ? 'Actualizar' : 'Crear'}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>

          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Listado
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense sx={{ maxHeight: 300, overflow: 'auto', p: 0 }}>
              {dinamic_texts_list && dinamic_texts_list.length > 0 ? (
                dinamic_texts_list.map((item: any) => {
                  const isActive = item.status === 1 || item.status === undefined;
                  return (
                    <ListItem 
                      key={item.id || item.value} 
                      sx={{ 
                        bgcolor: isActive ? 'transparent' : '#f9f9f9', 
                        borderRadius: 1, 
                        mb: 1,
                        border: '1px solid',
                        borderColor: isActive ? 'divider' : '#eeeeee'
                      }}
                    >
                      <ListItemText 
                        primary={item.name || item.label} 
                        secondary={
                          <Chip 
                            label={isActive ? 'Activo' : 'Inactivo'} 
                            size="small" 
                            sx={{ 
                              mt: 0.5, 
                              height: 20, 
                              fontSize: '0.7rem',
                              bgcolor: isActive ? '#E4F6D6' : '#FFE2E3',
                              color: isActive ? '#5DCC0A' : '#FF4C51',
                              fontWeight: 600
                            }} 
                          />
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          color="primary" 
                          size="small" 
                          onClick={() => {
                            setEditMode(true);
                            setSelectedText(item);
                          }}
                          disabled={!isActive}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {isActive ? (
                          <IconButton 
                            edge="end" 
                            color="error" 
                            size="small" 
                            onClick={async () => {
                              const res = await dispatch(deleteDinamicTextStore(item.id || item.value));
                              handleResponse(res, 'Desactivación', true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton 
                            edge="end" 
                            color="success" 
                            size="small" 
                            onClick={async () => {
                              const res = await dispatch(restoreDinamicTextStore(item.id || item.value));
                              handleResponse(res, 'Restauración', true);
                            }}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 3 }}>
                  No hay registros creados
                </Typography>
              )}
            </List>
          </Box>
        </DialogContent>
      </Dialog>
      {ResponseComponents}
    </>
  );
};
